import React, { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { getIdToken, reload, sendEmailVerification } from 'firebase/auth';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { pendingEmailProfileKey, requiresEmailVerification } from './authVerification';
import { recordLegalAcceptance } from './userProfile';
import { ROUTES } from './routes';

const RESEND_COOLDOWN_SECONDS = 60;

function getSafeReturnPath(value) {
  if (!value || typeof value !== 'string' || !value.startsWith('/')) {
    return ROUTES.mainView;
  }
  if (value.startsWith('//') || value.startsWith(ROUTES.login) || value.startsWith(ROUTES.register) || value.startsWith(ROUTES.verifyEmail)) {
    return ROUTES.mainView;
  }
  return value;
}

function verificationErrorMessage(error) {
  if (error?.code === 'auth/too-many-requests') {
    return 'Too many verification email requests. Wait a few minutes before trying again.';
  }
  if (error?.code === 'auth/network-request-failed') {
    return 'Network error while contacting Firebase. Check your connection and try again.';
  }
  return 'JourneyBot could not send the verification email. Try again later.';
}

export default function EmailVerificationRequired() {
  const { authState, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [checking, setChecking] = useState(false);
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const user = authState.user;
  const returnTo = useMemo(
    () => getSafeReturnPath(location.state?.from?.pathname || new URLSearchParams(location.search).get('returnTo')),
    [location.search, location.state],
  );

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = window.setInterval(() => {
      setCooldown((value) => Math.max(0, value - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [cooldown]);

  if (authState.loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Checking your account...</Typography>
      </Box>
    );
  }

  if (!user) {
    return <Navigate to={ROUTES.login} replace state={{ from: location }} />;
  }

  if (!requiresEmailVerification(user)) {
    return <Navigate to={returnTo} replace />;
  }

  const completePendingProfile = async () => {
    const stored = window.sessionStorage?.getItem(pendingEmailProfileKey(user.uid));
    if (!stored) return;

    const pending = JSON.parse(stored);
    await recordLegalAcceptance(user, {
      name: pending.name,
      authProvider: pending.authProvider || 'local',
    });
    window.sessionStorage?.removeItem(pendingEmailProfileKey(user.uid));
  };

  const handleVerified = async () => {
    setChecking(true);
    setStatusMessage('');
    setErrorMessage('');
    try {
      await reload(user);
      if (!user.emailVerified) {
        setStatusMessage('That email address is not verified yet. Open the verification email and click the link, then try again.');
        return;
      }

      await getIdToken(user, true);
      await completePendingProfile();
      navigate(returnTo, { replace: true });
    } catch (error) {
      console.error(error);
      setErrorMessage('JourneyBot could not refresh your verification status. Try again.');
    } finally {
      setChecking(false);
    }
  };

  const handleResend = async () => {
    setSending(true);
    setStatusMessage('');
    setErrorMessage('');
    try {
      await sendEmailVerification(user);
      setCooldown(RESEND_COOLDOWN_SECONDS);
      setStatusMessage('Verification email sent. Check your inbox and spam or junk folders.');
    } catch (error) {
      console.error(error);
      setErrorMessage(verificationErrorMessage(error));
    } finally {
      setSending(false);
    }
  };

  return (
    <Box
      sx={{
        maxWidth: 560,
        mx: 'auto',
        px: 2,
        py: { xs: 4, sm: 6 },
        textAlign: 'left',
      }}
      data-testid="email-verification-page"
    >
      <Stack spacing={2.5}>
        <Typography variant="h4" component="h1">
          Verify your email
        </Typography>
        <Typography>
          JourneyBot needs you to verify {user.email || 'your email address'} before you can access journeys,
          cloud saves, or protected JourneyBot APIs.
        </Typography>
        <Typography>
          Open the Firebase verification email and click the link. Check your spam or junk folders if it is not in your inbox.
        </Typography>
        {statusMessage ? <Alert severity="info">{statusMessage}</Alert> : null}
        {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Button variant="contained" onClick={handleVerified} disabled={checking || sending} data-testid="email-verified">
            {checking ? 'Checking...' : "I've verified my email"}
          </Button>
          <Button
            variant="outlined"
            onClick={handleResend}
            disabled={sending || checking || cooldown > 0}
            data-testid="resend-verification"
          >
            {cooldown > 0 ? `Resend in ${cooldown}s` : sending ? 'Sending...' : 'Resend verification email'}
          </Button>
          <Button variant="text" onClick={logout} disabled={checking || sending} data-testid="use-different-email">
            Use a different email
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

